import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'formik';
import TaskIcon from '@mui/icons-material/TaskOutlined';
import PhotoCameraIcon from '@mui/icons-material/PhotoCameraOutlined';
import { Stack, Typography } from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';

import ModalWindow from '@ui-lib/components/ModalWindow';
import Button from '@ui-lib/components/Button';
import Card from '@ui-lib/components/Card';
import { createSx } from '@ui-lib/utils';
import { useModal } from '@web-shared/hooks';
import { CreateBolFormValues } from '../../const';
import CameraPreview from './CameraPreview';
import { PhotosContainer } from './PhotosContainer';

const sx = createSx({
  container: {
    width: '100%',
    alignSelf: 'center',
    boxSizing: 'border-box',
  }
});

export const PhotoField = connect<Record<string, unknown>, CreateBolFormValues>(({ formik: { setFieldValue, values } }) => {
  const { t } = useTranslation();
  const [open, handleModalStateChange] = useModal();
  const [cameraError, setCameraError] = useState<string | null>(null);

  const savePhoto = (imageSrc: string) => {
    const value = [...values.photos, imageSrc];
    setFieldValue('photos', value);
  };

  const photos = values.photos;

  const hasPhotos = photos.length > 0;

  const takePhotoButton = <Button
    variant='tonal'
    fullWidth
    onClick={handleModalStateChange}
    startIcon={<PhotoCameraIcon />}
  >
    {t(hasPhotos ? 'bolCreatePage_addMorePhotos' : 'bolCreatePage_takePhoto')}
  </Button>;

  return (
    <>
      <Stack sx={sx.container} spacing={2}>
        {hasPhotos ? (
          <>
            <PhotosContainer />
            {takePhotoButton}
          </>
        ) : (
          <Card testID='photo-field' >
            <Stack alignItems='center' gap={1}>
              {cameraError ? (
                <>
                  <ErrorOutlineOutlinedIcon color='error' fontSize='large' />
                  <Typography variant='subtitle1Bold'>{t('error')}</Typography>
                  <Typography variant='body2' color='secondary'>{cameraError}</Typography>
                  {takePhotoButton}
                </>
              ) : (
                <>
                  <TaskIcon fontSize='large' color='primary' />
                  <Typography variant='body2' gutterBottom textAlign='center'>
                    {t('bolCreatePage_photoStepDescription')}
                  </Typography>
                  {takePhotoButton}
                </>
              )}
            </Stack>
          </Card>
        )}
      </Stack>
      <ModalWindow
        testID='modal-window'
        open={open}
        onClose={handleModalStateChange}>
        <CameraPreview
          onClose={handleModalStateChange}
          onCapture={savePhoto}
          cameraError={cameraError}
          setCameraError={setCameraError}
        />
      </ModalWindow>
    </>
  );
});
